import { motion } from "framer-motion"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import { Checkbox } from "@/clientWeb/components/ui/checkbox"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/clientWeb/components/ui/select"
import {
  useSettingsStore,
  CALL_RINGTONE_OPTIONS,
  MESSAGE_TONE_OPTIONS,
  TONE_LABEL_KEYS,
  type NotificationPrefId,
} from "."

type NotificationOption = {
  id: string
  label: string
  description: string
}

type NotificationGroup = {
  id: string
  title: string
  subtitle: string
  options: NotificationOption[]
}

const getGroups = (t: (key: string) => string): NotificationGroup[] => [
  {
    id: "chat",
    title: t("settings.general.chatNotificationsTitle"),
    subtitle: t("settings.general.chatNotificationsSubtitle"),
    options: [
      {
        id: "enable-chat-notifications",
        label: t("settings.general.enableChatNotifications"),
        description: t("settings.general.enableChatNotificationsDesc"),
      },
      {
        id: "play-sound-chat",
        label: t("settings.general.playSoundForChat"),
        description: t("settings.general.playSoundForChatDesc"),
      },
      {
        id: "incoming-calls-sound",
        label: t("settings.general.incomingCallsSound"),
        description: t("settings.general.incomingCallsSoundDesc"),
      },
    ],
  },
  {
    id: "participant",
    title: t("settings.general.participantNotificationsTitle"),
    subtitle: t("settings.general.participantNotificationsSubtitle"),
    options: [
      {
        id: "participant-join-alert",
        label: t("settings.general.participantJoinAlert"),
        description: t("settings.general.participantJoinAlertDesc"),
      },
      {
        id: "participant-leave-alert",
        label: t("settings.general.participantLeaveAlert"),
        description: t("settings.general.participantLeaveAlertDesc"),
      },
      {
        id: "participant-sound",
        label: t("settings.general.playSoundForParticipantActivity"),
        description: t("settings.general.playSoundForParticipantActivityDesc"),
      },
    ],
  },
  {
    id: "general",
    title: t("settings.general.generalControlsTitle"),
    subtitle: t("settings.general.generalControlsSubtitle"),
    options: [
      {
        id: "mute-all-sounds",
        label: t("settings.general.muteAllSounds"),
        description: t("settings.general.muteAllSoundsDesc"),
      },
      {
        id: "do-not-disturb",
        label: t("settings.general.doNotDisturbMode"),
        description: t("settings.general.doNotDisturbModeDesc"),
      },
    ],
  },
  {
    id: "meeting",
    title: t("settings.general.meetingNotificationsTitle"),
    subtitle: t("settings.general.meetingNotificationsSubtitle"),
    options: [
      {
        id: "meeting-reminders",
        label: t("settings.general.meetingReminder"),
        description: t("settings.general.meetingReminderDesc"),
      },
      {
        id: "meeting-reminder-sound",
        label: t("settings.general.meetingReminderSound"),
        description: t("settings.general.meetingReminderSoundDesc"),
      },
      {
        id: "turn-off-reminders",
        label: t("settings.general.turnOffMeetingReminders"),
        description: t("settings.general.turnOffMeetingRemindersDesc"),
      },
    ],
  },
]

function GroupBlock({
  group,
  checkedMap,
  onToggle,
  index,
}: {
  group: NotificationGroup
  checkedMap: Record<string, boolean>
  onToggle: (id: string, checked: boolean) => void
  index: number
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.22, ease: "easeOut" }}
      className="rounded-xl border border-border/70 bg-muted/20 p-4"
    >
      <h3 className="text-md font-semibold text-foreground">{group.title}</h3>
      <p className="text-xs text-muted-foreground mt-0.5 mb-5">{group.subtitle}</p>

      <div className="space-y-2.5">
        {group.options.map((option) => (
          <label
            key={option.id}
            className="flex py-1 items-start gap-2.5 text-sm text-foreground cursor-pointer"
          >
            <Checkbox
              id={option.id}
              checked={checkedMap[option.id] ?? false}
              onCheckedChange={(checked) => onToggle(option.id, checked === true)}
            />
            <div className="-mt-0.5">
              <p className="text-md text-foreground leading-5">{option.label}</p>
              <p className="text-sm text-muted-foreground leading-4 mt-0.5">{option.description}</p>
            </div>
          </label>
        ))}
      </div>
    </motion.div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function GeneralSettings() {
  const t = useTranslation()
  const GROUPS = getGroups(t)

  // All toggles are backed by the persisted settings store. Reading + writing
  // through `useSettingsStore` means a flip here propagates instantly to the
  // useMeetingReminders and every other subscriber.
  const notifications = useSettingsStore((s) => s.settings.notifications)
  const sounds = useSettingsStore((s) => s.settings.sounds)
  const setOption = useSettingsStore((s) => s.setOption)
  const setSounds = useSettingsStore((s) => s.setSounds)

  const checkedMap = notifications as unknown as Record<string, boolean>

  // A stored value from an older build may not be a current option: show it raw.
  const toneLabel = (tone: string) => {
    const key = TONE_LABEL_KEYS[tone as keyof typeof TONE_LABEL_KEYS]
    return key ? t(key) : tone
  }

  const handleToggle = (id: string, checked: boolean) => {
    setOption(id as NotificationPrefId, checked)
    const label = GROUPS.flatMap(g => g.options).find(o => o.id === id)?.label
    toast.info({
      title: label
        ? t(checked ? "settings.general.optionEnabled" : "settings.general.optionDisabled", { label })
        : t(checked ? "auth.enabled" : "auth.disabled"),
    })
  }

  const callRingtone = sounds.callRingtone
  const messageTone = sounds.messageTone
  const setCallRingtone = (v: string) => { setSounds({ callRingtone: v }); toast.info({ title: t("settings.general.ringtoneSet", { tone: toneLabel(v) }) }) }
  const setMessageTone = (v: string) => { setSounds({ messageTone: v }); toast.info({ title: t("settings.general.messageToneSet", { tone: toneLabel(v) }) }) }

  return (
    <div className="space-y-2">
      {/* Page header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="mb-5"
      >
        <h2 className="text-lg font-bold text-foreground">{t("settings.notificationsAlerts")}</h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          {t("settings.general.pageSubtitle")}
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-card rounded-xl border border-stroke dark:border-border overflow-hidden"
      >
        <div className="h-1 w-full bg-gradient-to-r from-emerald-500 to-teal-500" />



        <div className="p-5 space-y-3.5">
          {GROUPS.map((group, idx) => (
            <GroupBlock
              key={group.id}
              group={group}
              checkedMap={checkedMap}
              onToggle={handleToggle}
              index={idx}
            />
          ))}

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: GROUPS.length * 0.05, duration: 0.22, ease: "easeOut" }}
            className="rounded-xl border border-border/70 bg-muted/20 p-4"
          >
            <h3 className="text-sm font-semibold text-foreground">{t("settings.general.incomingCallSoundsTitle")}</h3>
            <p className="text-xs text-muted-foreground mt-0.5 mb-3">
              {t("settings.general.incomingCallSoundsSubtitle")}
            </p>

            <div className="space-y-3">
              <div className="space-y-1.5">
                <p className="text-sm text-foreground">{t("settings.general.callRingtone")}</p>
                <Select value={callRingtone} onValueChange={setCallRingtone}>
                  <SelectTrigger className="!w-full h-9">
                    <SelectValue placeholder={t("settings.general.selectCallRingtone")} />
                  </SelectTrigger>
                  <SelectContent>
                    {CALL_RINGTONE_OPTIONS.map((tone) => (
                      <SelectItem key={tone} value={tone}>
                        {toneLabel(tone)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <p className="text-sm text-foreground">{t("settings.general.messageTone")}</p>
                <Select value={messageTone} onValueChange={setMessageTone}>
                  <SelectTrigger className="!w-full h-9">
                    <SelectValue placeholder={t("settings.general.selectMessageTone")} />
                  </SelectTrigger>
                  <SelectContent>
                    {MESSAGE_TONE_OPTIONS.map((tone) => (
                      <SelectItem key={tone} value={tone}>
                        {toneLabel(tone)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  )
}
